import { useMemo } from 'react';
import type { API } from '@trinity/chat';

type QuantNode = API.ProjectQuantFolders.GetQuantDirectoryTree.QuantNode;
type ProjectQuantAnalysisItemRecord = API.ProjectsQuantAnalysis.ProjectQuantAnalysisItemRecord;
type QuantAnalysisItem = API.ProjectsQuantAnalysis.QuantAnalysisItem;

type UseSearchFilterOptions = {
  searchValue: string;
  options: QuantAnalysisItem[];
  folderTreeData?: QuantNode[];
  isSearchable?: boolean;
};

type SearchResult = {
  folderId: string;
  item: { id: number; name: string };
  isMatch: boolean;
};

type FolderSearchResult = {
  folderId: string;
  folderName: string;
  items: SearchResult[];
  hasMatches: boolean;
  shouldExpand: boolean;
};

type UseSearchFilterReturn = {
  filteredOptions: QuantAnalysisItem[];
  folderResults: FolderSearchResult[];
  hasResults: boolean;
};

const findQuantAnalysisInFolder = (
  node: QuantNode,
  searchTerm: string,
): SearchResult[] => {
  const results: SearchResult[] = [];
  if (node.item.type !== 'folder') return results;

  const folder = node.item.item;
  const folderId = String(folder.id);

  // Find analysis items that are direct children of this folder
  node.children
    .filter(child => child.item.type === 'analyses')
    .forEach(child => {
      const analysis = child.item.item as ProjectQuantAnalysisItemRecord;
      const name = analysis.name?.toLowerCase() || '';
      const isMatch = searchTerm ? name.includes(searchTerm) : false;

      results.push({
        folderId,
        item: { id: analysis.id, name: analysis.name },
        isMatch,
      });
    });

  return results;
};

const getFolderSearchResults = (
  folderTreeData: QuantNode[],
  searchTerm: string,
): FolderSearchResult[] => {
  const results: FolderSearchResult[] = [];

  // recursive function to return content of the folders
  const processNode = (node: QuantNode) => {
    if (node.item.type !== 'folder') return;

    const folder = node.item.item;
    const items = findQuantAnalysisInFolder(node, searchTerm);
    const hasMatches = items.some(item => item.isMatch);

    results.push({
      folderId: String(folder.id),
      folderName: folder.name,
      items,
      hasMatches,
      shouldExpand: searchTerm ? hasMatches : false,
    });

    // Process child folders recursively
    node.children
      .filter(child => child.item.type === 'folder')
      .forEach(processNode);
  };

  folderTreeData.forEach(processNode);
  return results;
};

export const useSearchFilter = ({
  searchValue,
  options,
  folderTreeData,
  isSearchable = true,
}: UseSearchFilterOptions): UseSearchFilterReturn => {
  const { filteredOptions, folderResults } = useMemo(() => {
    if (!isSearchable || !searchValue.trim()) {
      return {
        filteredOptions: options,
        folderResults: folderTreeData ? getFolderSearchResults(folderTreeData, '') : [],
      };
    }

    const searchTerm = searchValue.toLowerCase().trim();

    const filteredOptions = options.filter(option => {
      const name = option.name.toLowerCase();
      return name.includes(searchTerm);
    });

    // Get folder search results
    const folderResults = folderTreeData ? getFolderSearchResults(folderTreeData, searchTerm) : [];

    return {
      filteredOptions,
      folderResults,
    };
  }, [searchValue, options, folderTreeData, isSearchable]);

  const hasResults = filteredOptions.length > 0 || folderResults.some(folder => folder.hasMatches);

  return {
    filteredOptions,
    folderResults,
    hasResults,
  };
};
